import { graphql } from "babel-plugin-relay/macro";
import { useCallback, useContext } from "react";
import { useMutation } from "react-relay";
import {
  ScreensGeneratedContentInput,
  useBrandUpdateGeneratedContentMutation$data,
  useBrandUpdateGeneratedContentMutation as useBrandUpdateGeneratedContentMutationType,
} from "./__generated__/useBrandUpdateGeneratedContentMutation.graphql";
import SnackbarContext from "../contexts/SnackbarContext";

const mutation = graphql`
  mutation useBrandUpdateGeneratedContentMutation(
    $brandId: ID!
    $data: ScreensGeneratedContentInput!
  ) {
    saveAllScreensGeneratedText(brandId: $brandId, data: $data)
  }
`;

const useBrandUpdateGeneratedContentMutation = (): [
  (
    brandId: string,
    data: ScreensGeneratedContentInput,
    onCompleted?: (data: useBrandUpdateGeneratedContentMutation$data) => void,
    onErrorMessages?: (
      errors: Array<{
        field: string;
        message: string;
      }>
    ) => void
  ) => void,
  boolean
] => {
  const [saveMutation, isMutationInFlight] =
    useMutation<useBrandUpdateGeneratedContentMutationType>(mutation);

  const snackbarContext = useContext(SnackbarContext);

  const onMutate = useCallback(
    (
      brandId: string,
      data: ScreensGeneratedContentInput,
      onCompleted?: (data: useBrandUpdateGeneratedContentMutation$data) => void,
      onErrorMessages?: (
        errors: Array<{
          field: string;
          message: string;
        }>
      ) => void
    ) => {
      saveMutation({
        variables: {
          brandId,
          data,
        },
        onCompleted: (data, errors) => {
          if (errors) {
            const handledErrors = errors
              .filter((error) => error.message)
              .map((error) => ({
                field: "saveAllText",
                message: error.message,
              }));

            if (handledErrors.length > 0) {
              snackbarContext?.openSnackbar("Failed to save text", "error");
              onErrorMessages && onErrorMessages(handledErrors);
            } else {
              snackbarContext?.openSnackbar("Failed to save text", "error");
            }
          } else {
            onCompleted && onCompleted(data);
            snackbarContext?.openSnackbar(
              "All screens text saved successfully",
              "success"
            );
          }
        },
        onError: (error: Error) => {
          snackbarContext?.openSnackbar("Failed to save text", "error");
        },
      });
    },
    [snackbarContext, saveMutation]
  );

  return [onMutate, isMutationInFlight];
};

export default useBrandUpdateGeneratedContentMutation;
